import React, { useContext, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../Datas/siteConfig";
import "../css/checkout.css";

const initialFormValues = {
  email: "",
  country: "Hong Kong",
  firstName: "",
  lastName: "",
  address: "",
  address2: "",
  postalCode: "",
  city: "",
  region: "",
  shippingMethod: "standard",
};

const getProductById = (id) =>
  productData.find((product) => String(product.id) === String(id));

const validateForm = (values) => {
  const validationErrors = {};

  if (!/^\S+@\S+\.\S+$/.test(values.email.trim())) {
    validationErrors.email = "Enter a valid email address.";
  }
  if (!values.firstName.trim()) validationErrors.firstName = "Enter your first name.";
  if (!values.lastName.trim()) validationErrors.lastName = "Enter your last name.";
  if (!values.address.trim()) validationErrors.address = "Enter a delivery address.";
  if (!values.postalCode.trim()) validationErrors.postalCode = "Enter a postal code.";
  if (!values.city.trim()) validationErrors.city = "Enter a city.";
  if (!values.country) validationErrors.country = "Choose a country or region.";
  return validationErrors;
};

const CheckOut = () => {
  const formRef = useRef(null);
  const { cartItems } = useContext(CartContext);
  const [formValues, setFormValues] = useState(initialFormValues);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const items = useMemo(
    () =>
      cartItems
        .map((item) => ({ ...item, product: getProductById(item.id) }))
        .filter((item) => item.product),
    [cartItems]
  );
  const subtotal = useMemo(
    () =>
      items.reduce(
        (total, item) => total + Number(item.product.price) * item.quantity,
        0
      ),
    [items]
  );
  const shipping = siteConfig.defaultShipping;
  const total = subtotal + shipping;

  const updateField = (event) => {
    const { name, type, checked, value } = event.target;
    setFormValues((currentValues) => ({
      ...currentValues,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((currentErrors) => {
      if (!currentErrors[name]) return currentErrors;
      const nextErrors = { ...currentErrors };
      delete nextErrors[name];
      return nextErrors;
    });
    setSubmitError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    const validationErrors = validateForm(formValues);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      setSubmitError("Please review the highlighted fields before continuing.");
      const firstInvalidField = Object.keys(validationErrors)[0];
      if (typeof window !== "undefined") {
        window.requestAnimationFrame(() => {
          formRef.current
            ?.querySelector(`[name="${firstInvalidField}"]`)
            ?.focus();
        });
      }
      return;
    }

    setSubmitting(true);
    setSubmitError("");

    try {
      const randomPart = window.crypto?.getRandomValues
        ? Array.from(window.crypto.getRandomValues(new Uint32Array(3))).join("")
        : Math.floor(Math.random() * 1e15).toString();
      const orderNo = `${Date.now()}${randomPart}`.slice(0, 28);
      const origin = window.location.origin;
      const params = new URLSearchParams({
        orderNo,
        amount: total.toFixed(2),
        currency: siteConfig.currency,
        email: formValues.email.trim(),
        success_url: `${origin}/payment?state=success&orderNo=${encodeURIComponent(orderNo)}`,
        cancel_url: `${origin}/payment?state=cancel&orderNo=${encodeURIComponent(orderNo)}`,
        title: items[0].product.name,
      });
      window.location.assign(`${origin}/create_payment.php?${params.toString()}`);
    } catch {
      setSubmitting(false);
      setSubmitError("We could not open the secure payment page. Please try again.");
    }
  };

  const errorMessage = (fieldName) =>
    errors[fieldName] ? (
      <span className="checkout-page__field-error" id={`checkout-${fieldName}-error`}>
        {errors[fieldName]}
      </span>
    ) : null;

  if (items.length === 0) {
    return (
      <main className="checkout-page checkout-page--empty" id="main-content">
        <section className="checkout-page__empty-state" aria-labelledby="checkout-empty-title">
          <p>Secure checkout</p>
          <h1 id="checkout-empty-title">There is nothing to check out yet</h1>
          <p>Add a piece to your bag and return here when you are ready.</p>
          <Link to="/shop">Continue shopping</Link>
        </section>
      </main>
    );
  }

  return (
    <main className="checkout-page" id="main-content">
      <header className="checkout-page__header">
        <Link to="/" className="checkout-page__wordmark">
          {siteConfig.wordmark}
        </Link>
        <div>
          <span aria-hidden="true">●</span>
          Secure checkout
        </div>
        <Link to="/cartpage">Return to bag</Link>
      </header>

      <form
        ref={formRef}
        className="checkout-page__layout"
        onSubmit={handleSubmit}
        noValidate
      >
        <div className="checkout-page__flow">
          <div className="checkout-page__intro">
            <p>Almost yours</p>
            <h1>Checkout</h1>
          </div>

          {submitError && (
            <div className="checkout-page__form-alert" role="alert">
              {submitError}
            </div>
          )}

          <section className="checkout-page__section" aria-labelledby="contact-heading">
            <div className="checkout-page__section-heading">
              <span>01</span>
              <div>
                <h2 id="contact-heading">Contact</h2>
                <p>We will send your order updates here.</p>
              </div>
            </div>

            <div className="checkout-page__fields">
              <label className="checkout-page__field checkout-page__field--wide">
                <span>Email address *</span>
                <input
                  type="email"
                  name="email"
                  value={formValues.email}
                  onChange={updateField}
                  autoComplete="email"
                  aria-invalid={Boolean(errors.email)}
                />
                {errorMessage("email")}
              </label>

            </div>
          </section>

          <section className="checkout-page__section" aria-labelledby="delivery-heading">
            <div className="checkout-page__section-heading">
              <span>02</span>
              <div>
                <h2 id="delivery-heading">Delivery</h2>
                <p>Where should we send your hand-painted pieces?</p>
              </div>
            </div>

            <div className="checkout-page__fields">
              <label className="checkout-page__field checkout-page__field--wide">
                <span>Country or region *</span>
                <select
                  name="country"
                  value={formValues.country}
                  onChange={updateField}
                  autoComplete="country-name"
                  aria-invalid={Boolean(errors.country)}
                >
                  <option value="Hong Kong">Hong Kong</option>
                  <option value="Australia">Australia</option>
                  <option value="Austria">Austria</option>
                  <option value="Belgium">Belgium</option>
                  <option value="Canada">Canada</option>
                  <option value="Denmark">Denmark</option>
                  <option value="France">France</option>
                  <option value="Germany">Germany</option>
                  <option value="Italy">Italy</option>
                  <option value="Japan">Japan</option>
                  <option value="Malaysia">Malaysia</option>
                  <option value="Netherlands">Netherlands</option>
                  <option value="New Zealand">New Zealand</option>
                  <option value="Portugal">Portugal</option>
                  <option value="Singapore">Singapore</option>
                  <option value="South Korea">South Korea</option>
                  <option value="Sweden">Sweden</option>
                  <option value="Switzerland">Switzerland</option>
                  <option value="Taiwan">Taiwan</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="United States">United States</option>
                </select>
                {errorMessage("country")}
              </label>

              <label className="checkout-page__field">
                <span>First name *</span>
                <input
                  type="text"
                  name="firstName"
                  value={formValues.firstName}
                  onChange={updateField}
                  autoComplete="given-name"
                  aria-invalid={Boolean(errors.firstName)}
                />
                {errorMessage("firstName")}
              </label>

              <label className="checkout-page__field">
                <span>Last name *</span>
                <input
                  type="text"
                  name="lastName"
                  value={formValues.lastName}
                  onChange={updateField}
                  autoComplete="family-name"
                  aria-invalid={Boolean(errors.lastName)}
                />
                {errorMessage("lastName")}
              </label>

              <label className="checkout-page__field checkout-page__field--wide">
                <span>Address *</span>
                <input
                  type="text"
                  name="address"
                  value={formValues.address}
                  onChange={updateField}
                  autoComplete="address-line1"
                  aria-invalid={Boolean(errors.address)}
                />
                {errorMessage("address")}
              </label>

              <label className="checkout-page__field checkout-page__field--wide">
                <span>Apartment, suite, etc. <small>Optional</small></span>
                <input
                  type="text"
                  name="address2"
                  value={formValues.address2}
                  onChange={updateField}
                  autoComplete="address-line2"
                />
              </label>

              <label className="checkout-page__field">
                <span>Postal code *</span>
                <input
                  type="text"
                  name="postalCode"
                  value={formValues.postalCode}
                  onChange={updateField}
                  autoComplete="postal-code"
                  aria-invalid={Boolean(errors.postalCode)}
                />
                {errorMessage("postalCode")}
              </label>

              <label className="checkout-page__field">
                <span>City *</span>
                <input
                  type="text"
                  name="city"
                  value={formValues.city}
                  onChange={updateField}
                  autoComplete="address-level2"
                  aria-invalid={Boolean(errors.city)}
                />
                {errorMessage("city")}
              </label>

              <label className="checkout-page__field checkout-page__field--wide">
                <span>Province / state <small>Optional</small></span>
                <input
                  type="text"
                  name="region"
                  value={formValues.region}
                  onChange={updateField}
                  autoComplete="address-level1"
                />
              </label>
            </div>

            <fieldset className="checkout-page__shipping-method">
              <legend>Shipping method</legend>
              <label>
                <input
                  type="radio"
                  name="shippingMethod"
                  value="standard"
                  checked={formValues.shippingMethod === "standard"}
                  onChange={updateField}
                />
                <span>
                  <strong>Standard tracked delivery</strong>
                  <small>Estimated 3–6 business days</small>
                </span>
                <b>{formatPrice(shipping)}</b>
              </label>
            </fieldset>
          </section>

          <section className="checkout-page__section" aria-labelledby="payment-heading">
            <div className="checkout-page__section-heading">
              <span>03</span>
              <div>
                <h2 id="payment-heading">Payment</h2>
                <p>You will continue to our secure payment provider.</p>
              </div>
            </div>

            <div className="checkout-page__payment-panel">
              <div className="checkout-page__payment-title">
                <span>Secure hosted payment</span>
                <div aria-label="Accepted cards">
                  <b>VISA</b>
                  <b>MC</b>
                  <b>AMEX</b>
                </div>
              </div>

              <p className="checkout-page__summary-note">
                Card details are entered only on the provider's payment page and are not collected by Reamflora.
              </p>
            </div>
          </section>

          <div className="checkout-page__submit-area">
            <button type="submit" disabled={submitting} aria-busy={submitting}>
              {submitting && <span className="checkout-page__spinner" aria-hidden="true" />}
              {submitting ? "Opening secure payment…" : `Pay now · ${formatPrice(total)}`}
            </button>
            <p>
              By placing this order, you agree to our <Link to="/terms">terms</Link>{" "}
              and acknowledge our <Link to="/privacy">privacy policy</Link>.
            </p>
          </div>
        </div>

        <aside className="checkout-page__summary" aria-labelledby="checkout-summary-title">
          <div className="checkout-page__summary-sticky">
            <div className="checkout-page__summary-heading">
              <h2 id="checkout-summary-title">Your order</h2>
              <Link to="/cartpage">Edit bag</Link>
            </div>

            <div className="checkout-page__summary-items">
              {items.map((item) => {
                const image = item.product.images?.[0] || item.product.image;
                return (
                  <article key={`${item.id}-${item.size}-${item.color}`}>
                    <div className="checkout-page__summary-image">
                      <img src={image} alt="" loading="lazy" decoding="async" />
                      <span>{item.quantity}</span>
                    </div>
                    <div>
                      <h3>{item.product.name}</h3>
                      <p>
                        {[item.color, item.size].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                    <strong>{formatPrice(item.product.price * item.quantity)}</strong>
                  </article>
                );
              })}
            </div>

            <dl className="checkout-page__totals">
              <div>
                <dt>Subtotal</dt>
                <dd>{formatPrice(subtotal)}</dd>
              </div>
              <div>
                <dt>Shipping</dt>
                <dd>{formatPrice(shipping)}</dd>
              </div>
              <div>
                <dt>Total</dt>
                <dd>
                  <small>{siteConfig.currency}</small> {formatPrice(total)}
                </dd>
              </div>
            </dl>

            <p className="checkout-page__summary-note">
              Taxes included. <Link to="/returns">Shipping & returns information</Link>
            </p>
          </div>
        </aside>
      </form>
    </main>
  );
};

export default CheckOut;
